import { Box, Flex, useColorModeValue, Image } from '@chakra-ui/react';
import React from 'react';
import { useInView } from 'react-intersection-observer';

import config from 'configs/app';
import { useScrollDirection } from 'lib/contexts/scrollDirection';
import RewardsButton from 'ui/rewards/RewardsButton';
import IconSvg from 'ui/shared/IconSvg';
import SearchBar from 'ui/snippets/searchBar/SearchBar';
import UserProfileMobile from 'ui/snippets/user/profile/UserProfileMobile';
import UserWalletMobile from 'ui/snippets/user/wallet/UserWalletMobile';

import Burger from './Burger';

type Props = {
  hideSearchBar?: boolean;
  renderSearchBar?: () => React.ReactNode;
};

const HeaderMobile = ({ hideSearchBar, renderSearchBar }: Props) => {
  const bgColor = useColorModeValue('white', '#111111');
  const iconColor = useColorModeValue('gray.600', 'white');
  const searchBg = useColorModeValue('white', '#111111');
  const logoSrc = useColorModeValue(
    '/static/homepage/sidebar-logo-light.svg',
    '/static/homepage/sidebar-logo.svg',
  );
  const scrollDirection = useScrollDirection();
  const { ref, inView } = useInView({ threshold: 1 });
  const [ isSearchOpen, setIsSearchOpen ] = React.useState(false);
  const handleSearchToggle = React.useCallback(() => setIsSearchOpen(v => !v), []);

  const searchBar = renderSearchBar ? renderSearchBar() : <SearchBar/>;

  return (
    <Box
      ref={ ref }
      bgColor={ bgColor }
      display={{ base: 'block', lg: 'none' }}
      position="sticky"
      top="-1px"
      left={ 0 }
      zIndex="sticky2"
      pt="1px"
    >
      <Flex
        as="header"
        px={ 4 }
        py={ 0 }
        height="64px"
        bgColor={ bgColor }
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        transitionProperty="box-shadow"
        transitionDuration="slow"
        boxShadow={ !inView && scrollDirection === 'down' ? 'md' : 'none' }
      >
        { /* Logo - 左侧 */ }
        <Box flexShrink={ 0 }>
          <Image
            src={ logoSrc }
            alt="BOT"
            h="32px"
            w="auto"
          />
        </Box>

        { /* 右侧：钱包 + 搜索 + 汉堡菜单 */ }
        <Flex alignItems="center" gap="4px">
          { config.features.rewards.isEnabled && <RewardsButton/> }
          {
            (config.features.account.isEnabled && <UserProfileMobile/>) ||
            (config.features.blockchainInteraction.isEnabled && <UserWalletMobile/>) ||
            null
          }
          { !hideSearchBar && (
            <Box
              padding={ 2 }
              cursor="pointer"
              onClick={ handleSearchToggle }
              color={ isSearchOpen ? '#14DCAC' : iconColor }
            >
              <IconSvg
                name="search"
                boxSize={ 6 }
                display="block"
                aria-label="Search"
              />
            </Box>
          ) }
          <Burger/>
        </Flex>
      </Flex>

      { /* 搜索栏：点击搜索图标后推出，覆盖内部 absolute 定位使其撑开空间 */ }
      { !hideSearchBar && isSearchOpen && (
        <Box
          bg={ searchBg }
          px={ 4 }
          py={ 2 }
          sx={{
            '& form': {
              position: 'relative !important',
              top: '0 !important',
              left: '0 !important',
              zIndex: 'auto !important',
              transform: 'none !important',
              padding: '0 !important',
              boxShadow: 'none !important',
            },
          }}
        >
          { searchBar }
        </Box>
      ) }
    </Box>
  );
};

export default React.memo(HeaderMobile);
